import React from 'react';
import { Mountain } from 'lucide-react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { formatCurrency } from '../../lib/utils';

const RATE_LABELS = {
  grain: 'Grain & agri', coal: 'Coal', iron_ore: 'Iron ore', bauxite: 'Bauxite', cement: 'Cement', fertilizer: 'Fertilizer',
  sugar: 'Raw sugar', steel_products: 'Steel products', project_cargo: 'Project cargo', machinery: 'Machinery', timber: 'Timber', default: 'Other (default)'
};
const SURCHARGE_LABELS = {
  fuel_surcharge_percent: ['Bulk fuel surcharge', '% of freight'],
  port_charges_per_ton: ['Port charges', 'USD per MT'],
  stevedoring_per_ton: ['Stevedoring', 'USD per MT'],
  minimum_freight: ['Minimum freight', 'USD per fixture'],
};

export function BulkPricingPanel({ rates, surcharges, onRateChange, onSurchargeChange }) {
  const sampleMt = 30000;
  const grain = rates.grain || 0;
  const base = Math.max(grain * sampleMt, surcharges.minimum_freight || 0);
  const fuel = base * ((surcharges.fuel_surcharge_percent || 0) / 100);
  const port = ((surcharges.port_charges_per_ton || 0) + (surcharges.stevedoring_per_ton || 0)) * sampleMt;

  return (
    <div className="bg-white rounded-3xl shadow-card p-6 lg:col-span-2" data-testid="bulk-pricing-panel">
      <h2 className="text-xl font-medium tracking-[-0.01em] text-[#0F1B26] mb-2 flex items-center gap-2"><Mountain className="h-5 w-5 text-[#1F97CC]" /> Bulk Shipping Rates</h2>
      <p className="text-sm text-gray-500 mb-6">Freight per metric ton by commodity, plus bulk surcharges. Applied to all bulk quotes and bookings.</p>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3">
          {Object.keys(RATE_LABELS).map(function (k) {
            return (
              <div key={k} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-xl">
                <Label className="text-gray-700 font-medium text-sm">{RATE_LABELS[k]}</Label>
                <div className="flex items-center gap-1"><span className="text-gray-400 text-sm">$</span>
                  <Input type="number" min="0" step="0.5" value={rates[k] ?? ''} onChange={function (e) { onRateChange(k, e.target.value); }} className="w-24 h-10 rounded-lg text-right sv-mono" data-testid={"bulk-rate-" + k} />
                  <span className="text-gray-400 text-xs">/MT</span></div>
              </div>
            );
          })}
        </div>
        <div className="space-y-3">
          {Object.keys(SURCHARGE_LABELS).map(function (k) {
            return (
              <div key={k} className="p-3 bg-gray-50 rounded-xl">
                <Label className="text-gray-700 font-medium text-sm">{SURCHARGE_LABELS[k][0]}</Label>
                <p className="text-xs text-gray-500 mb-2">{SURCHARGE_LABELS[k][1]}</p>
                <Input type="number" min="0" step="0.5" value={surcharges[k] ?? ''} onChange={function (e) { onSurchargeChange(k, e.target.value); }} className="h-10 rounded-lg sv-mono" data-testid={"bulk-surcharge-" + k} />
              </div>
            );
          })}
          <div className="p-4 bg-[#0F1B26] text-white rounded-xl text-sm space-y-1" data-testid="bulk-sample-price">
            <p className="text-white/70 text-xs">Sample: 30,000 MT grain</p>
            <div className="flex justify-between"><span>Freight</span><span className="sv-mono">{formatCurrency(base)}</span></div>
            <div className="flex justify-between"><span>Fuel</span><span className="sv-mono">{formatCurrency(fuel)}</span></div>
            <div className="flex justify-between"><span>Port & stevedoring</span><span className="sv-mono">{formatCurrency(port)}</span></div>
            <div className="flex justify-between font-medium border-t border-white/20 pt-2 mt-2"><span>Total</span><span className="sv-mono text-blue-600">{formatCurrency(base + fuel + port)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
